import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Badge, Card, CardContent, CardHeader, CardTitle, Input, Label, PosButton, Segmented } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { NativeSelect } from '@/components/pos/office/NativeSelect'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { saveBranchPrice } from '@/db/repositories/catalogEdit'
import { useProducts } from '@/hooks/features/catalog'
import { useOfficeQuery, useOnline } from '@/hooks/features/office'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { OfficeModel } from '@/http/models'
import { syncEngine } from '@/sync/SyncEngine'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

type Tab = 'branch' | 'books' | 'offers'

/** Price books on the device (pulled), for picking which book's prices to edit; works offline. */
const usePriceBooks = () => {
  const sync = useSyncState()
  return useQuery({
    queryKey: ['price-books', sync.lastSyncedAt],
    queryFn: async () => (await rows('price_books').toArray()).filter((b) => !b.deleted_at).sort((a, b) => String(a.title).localeCompare(String(b.title))),
  }).data ?? []
}

/** One price cell, saved on Enter or when it loses focus; empty or 0 means "no price here, use the next in line". */
const PriceInput = ({ value, onSave, disabled }: { value?: number; onSave: (price: number) => Promise<void>; disabled?: boolean }) => {
  const [text, setText] = useState(value ? String(value) : '')
  const save = () => {
    const price = parseFloat(text) || 0
    if (price === (value ?? 0)) return
    onSave(price).catch((e: Error) => toast.error(e.message))
  }
  return (
    <Input
      inputMode="decimal"
      value={text}
      disabled={disabled}
      placeholder="—"
      onChange={(e) => setText(e.target.value.replace(/[^\d.]/g, ''))}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
      className="h-9 w-28 text-right tabular-nums"
    />
  )
}

/**
 * Branch prices and this branch's price-book prices are edited on the device and synced (POS.md §18.7 Q4); the books
 * themselves (business-wide) and offers are created online (§18.1 rule 4).
 */
export const PricingPage = () => {
  const { can } = useSession()
  const tabs = (
    [
      ['branch', 'Branch prices', can('product_variations:update') || can('product_variations:create')],
      ['books', 'Price books', can('price_books:list')],
      ['offers', 'Offers', can('offers:create')],
    ] as const
  ).filter(([, , ok]) => ok)
  const [tab, setTab] = useState<Tab>(tabs[0]?.[0] ?? 'branch')

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Prices" subtitle="Branch prices work offline; price books and offers need a connection.">
        {tabs.length > 1 && <Segmented<Tab> value={tab} onChange={setTab} options={tabs.map(([value, label]) => ({ value, label }))} />}
      </PageHeader>
      {tab === 'branch' && <BranchPrices />}
      {tab === 'books' && <PriceBooks />}
      {tab === 'offers' && <Offers />}
    </div>
  )
}

const BranchPrices = () => {
  const client = useQueryClient()
  const books = usePriceBooks()
  const [book, setBook] = useState('')
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)
  const { data, isLoading } = useProducts({ search: debounced || undefined })

  const save = (product: PosModel.Product, priceBookId: string | null) => async (price: number) => {
    await saveBranchPrice(product._id, priceBookId, price)
    client.invalidateQueries({ queryKey: ['products'] })
    toast.success(`${product.name}: ${price > 0 ? formatMoney(price) : 'price removed'}`)
  }

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, SKU or barcode" onCameraScan={setSearch} className="h-11 pl-9" />
        </div>
        {books.length > 0 && (
          <NativeSelect
            className="w-56"
            value={book}
            onChange={(e) => setBook(e.target.value)}
            options={[{ value: '', label: 'No price book' }, ...books.map((b) => ({ value: String(b._id), label: `Book: ${b.title}` }))]}
          />
        )}
      </div>
      <OfficeTable
        cards
        loading={isLoading}
        rows={data?.items ?? []}
        rowKey={(p) => p._id}
        columns={[
          { label: 'Product', cell: (p) => <span className="font-medium text-ink">{p.name}</span> },
          { label: 'SKU', className: 'hidden md:table-cell font-mono text-xs', cell: (p) => p.sku },
          { label: 'Regular', className: 'text-right tabular-nums', cell: (p) => formatMoney(p.regular_price ?? p.price) },
          {
            label: 'Branch price',
            className: 'text-right',
            cell: (p) => <PriceInput key={`${p._id}-branch-${p.price}`} value={p.branch_price} onSave={save(p, null)} />,
          },
          ...(book
            ? [
                {
                  label: books.find((b) => String(b._id) === book)?.title as string,
                  className: 'text-right',
                  cell: (p: PosModel.Product) => (
                    <PriceInput key={`${p._id}-${book}-${p.book_prices?.[book] ?? ''}`} value={p.book_prices?.[book]} onSave={save(p, book)} />
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  )
}

const PriceBooks = () => {
  const { can } = useSession()
  const online = useOnline()
  const client = useQueryClient()
  const { data, isFetching } = useOfficeQuery(['price-books'], () => OfficeModel.priceBooks())
  const [busy, setBusy] = useState(false)
  if (!online) return <NeedsConnection />

  const refresh = () => {
    client.invalidateQueries({ queryKey: ['office'] })
    // The till's copy (the book picker) comes with the next pull.
    syncEngine.requestSync()
  }

  const onCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const title = String(new FormData(form).get('title')).trim()
    setBusy(true)
    const res = await OfficeModel.createPriceBook({ title })
    setBusy(false)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not create the price book.')
    toast.success(`Price book ${title} created`)
    form.reset()
    refresh()
  }

  const toggle = async (id: string, status: string) => {
    const res = await OfficeModel.updatePriceBook(id, { status: status === 'active' ? 'inactive' : 'active' })
    if (!res.success) return toast.error(res.error?.message ?? 'Could not update the price book.')
    refresh()
  }

  const remove = async (id: string, title: string) => {
    const res = await OfficeModel.deletePriceBook(id)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not delete the price book.')
    toast.success(`${title} deleted`)
    refresh()
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <OfficeTable
          cards
          loading={isFetching}
          rows={data?.result ?? []}
          rowKey={(b) => String(b._id)}
          empty="No price books yet."
          columns={[
            { label: 'Price book', cell: (b) => <span className="font-medium text-ink">{b.title}</span> },
            { label: 'Status', cell: (b) => <Badge tone={b.status === 'active' ? 'success' : 'soft'}>{b.status}</Badge> },
            {
              label: '',
              className: 'text-right',
              cell: (b) => (
                <div className="flex justify-end gap-2">
                  {can('price_books:update') && (
                    <PosButton variant="outline" onClick={() => toggle(String(b._id), b.status)}>
                      {b.status === 'active' ? 'Deactivate' : 'Activate'}
                    </PosButton>
                  )}
                  {can('price_books:delete') && (
                    <PosButton variant="ghost" className="text-error" onClick={() => remove(String(b._id), b.title)}>
                      Delete
                    </PosButton>
                  )}
                </div>
              ),
            },
          ]}
        />
        {can('price_books:create') && (
          <Card>
            <CardHeader>
              <CardTitle>New price book</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onCreate} className="space-y-3">
                <Input name="title" placeholder="e.g. Wholesale" required className="h-11" />
                <p className="text-xs text-muted-foreground">Price books are business-wide; set their prices for this branch under Branch prices.</p>
                <PosButton type="submit" size="touch" className="w-full" disabled={busy}>
                  Create price book
                </PosButton>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

/** A simple offer on one product (`single_product`), for POS, this branch or all; richer offers are made in the admin. */
const Offers = () => {
  const { device } = useSession()
  const online = useOnline()
  const sync = useSyncState()
  const { data: products } = useProducts({})
  const { data: offers = [] } = useQuery({
    queryKey: ['offers', sync.lastSyncedAt],
    queryFn: async () => (await rows('offers').toArray()).filter((o) => !o.deleted_at && o.applies_to === 'pos'),
  })
  const [busy, setBusy] = useState(false)

  const onCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const f = new FormData(form)
    const startsAt = String(f.get('starts_at') || '')
    const endsAt = String(f.get('ends_at') || '')
    setBusy(true)
    const res = await OfficeModel.createOffer({
      name: String(f.get('name')).trim(),
      type: 'single_product',
      applies_to: 'pos',
      branch_id: f.get('scope') === 'branch' ? device?.branch_id : null,
      status: 'active',
      offer_rules: {
        product_variation_id: String(f.get('product')),
        discount_type: String(f.get('discount_type')),
        discount_value: parseFloat(String(f.get('discount_value'))) || 0,
      },
      ...(startsAt ? { starts_at: new Date(startsAt).toISOString() } : {}),
      ...(endsAt ? { ends_at: new Date(endsAt).toISOString() } : {}),
    })
    setBusy(false)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not create the offer.')
    toast.success('Offer created', { description: 'Devices apply it after their next sync.' })
    form.reset()
    syncEngine.requestSync()
  }

  // Products created on a till have no server id until they sync; an offer needs one.
  const choices = (products?.items ?? []).filter((p) => p.server_id)

  return (
    <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <OfficeTable
          cards
          rows={offers}
          rowKey={(o) => o.id}
          empty="No POS offers for this branch."
          columns={[
            { label: 'Offer', cell: (o) => <span className="font-medium text-ink">{String(o.name ?? '')}</span> },
            { label: 'Type', className: 'hidden md:table-cell', cell: (o) => String(o.type).replace(/_/g, ' ') },
            { label: 'Scope', cell: (o) => (o.branch_id ? 'This branch' : 'All branches') },
            { label: 'Status', cell: (o) => <Badge tone={o.status === 'active' ? 'success' : 'soft'}>{String(o.status)}</Badge> },
          ]}
        />
        <Card>
          <CardHeader>
            <CardTitle>New offer</CardTitle>
          </CardHeader>
          <CardContent>
            {!online ? (
              <p className="text-sm text-muted-foreground">Creating an offer needs a connection.</p>
            ) : (
              <form onSubmit={onCreate} className="space-y-3">
                <Input name="name" placeholder="Name, e.g. Rice 10% off" required minLength={3} className="h-11" />
                <NativeSelect name="product" required options={choices.map((p) => ({ value: p.server_id!, label: p.name }))} />
                <div className="flex gap-2">
                  <NativeSelect name="discount_type" className="w-36" options={[{ value: 'percentage', label: 'Percent' }, { value: 'cash', label: 'Amount' }]} />
                  <Input name="discount_value" inputMode="decimal" placeholder="Value" required className="h-11 text-right" />
                </div>
                <NativeSelect name="scope" options={[{ value: 'branch', label: 'This branch' }, { value: 'all', label: 'All branches' }]} />
                <div className="grid grid-cols-2 gap-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="starts_at">Starts</Label>
                    <Input id="starts_at" name="starts_at" type="datetime-local" className="h-11" />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="ends_at">Ends</Label>
                    <Input id="ends_at" name="ends_at" type="datetime-local" className="h-11" />
                  </div>
                </div>
                <PosButton type="submit" size="touch" className="w-full" disabled={busy}>
                  Create offer
                </PosButton>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
